import { useState, useEffect } from "react";
import { useAuth, apiClient } from "@/App";
import { toast } from "sonner";
import {
  PieChart,
  TrendingUp,
  TrendingDown,
  IndianRupee,
  Plus,
  Trash2,
  Wallet,
  Receipt,
  ArrowUpRight,
  ArrowDownRight,
  Download,
  Calculator,
  AlertTriangle,
  Lock,
  SquareAsterisk,
  RefreshCw
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { AddIncomeModal, AddExpenseModal, RateCalculatorModal, ClearDataModal, ClearEverythingModal } from "@/components/accounts/AccountsModals";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function Accounts() {
  const { isFounder } = useAuth();
  const [summary, setSummary] = useState(null);
  const [income, setIncome] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [payments, setPayments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [showIncomeModal, setShowIncomeModal] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [showCalculatorModal, setShowCalculatorModal] = useState(false);
  const [showClearDataModal, setShowClearDataModal] = useState(false);
  const [showClearEverythingModal, setShowClearEverythingModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Rate Calculator State
  const [calcForm, setCalcForm] = useState({
    area_sqft: "",
    rate_per_sqft: "",
    discount_percent: "0",
    gst_percentage: "18"
  });
  const [calcResult, setCalcResult] = useState(null);
  
  // Clear Data State
  const [clearForm, setClearForm] = useState({
    password: "",
    confirm_text: "",
    entities: []
  });
  
  // Clear Everything State
  const [clearEverythingForm, setClearEverythingForm] = useState({
    password: "",
    confirm_text: ""
  });
  
  const [incomeForm, setIncomeForm] = useState({
    category: "client_payment",
    description: "",
    amount: "",
    date: new Date().toISOString().split('T')[0],
    project_id: "",
    client_id: "",
    payment_id: "",
    payment_method: ""
  });
  
  const [expenseForm, setExpenseForm] = useState({
    category: "",
    description: "",
    amount: "",
    date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    if (isFounder) {
      fetchData();
    }
  }, [isFounder]);

  const fetchData = async () => {
    try {
      const [summaryRes, incomeRes, expensesRes, paymentsRes, projectsRes, clientsRes] = await Promise.all([
        apiClient.get("/accounts/summary"),
        apiClient.get("/income"),
        apiClient.get("/expenses"),
        apiClient.get("/payments?status=pending"),
        apiClient.get("/projects"),
        apiClient.get("/clients")
      ]);
      setSummary(summaryRes.data);
      setIncome(incomeRes.data);
      setExpenses(expensesRes.data);
      setPayments(paymentsRes.data);
      setProjects(projectsRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      console.error("Failed to fetch accounts data:", error);
      toast.error("Failed to load accounts data");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitIncome = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const data = {
        ...incomeForm,
        amount: parseFloat(incomeForm.amount),
        project_id: incomeForm.project_id || null,
        client_id: incomeForm.client_id || null,
        payment_id: incomeForm.payment_id || null
      };
      
      await apiClient.post("/income", data);
      toast.success("Income recorded successfully!");
      setShowIncomeModal(false);
      setIncomeForm({
        category: "client_payment",
        description: "",
        amount: "",
        date: new Date().toISOString().split('T')[0],
        project_id: "",
        client_id: "",
        payment_id: "",
        payment_method: ""
      });
      fetchData();
    } catch (error) {
      toast.error("Failed to record income");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitExpense = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await apiClient.post("/expenses", {
        ...expenseForm,
        amount: parseFloat(expenseForm.amount)
      });
      toast.success("Expense recorded successfully!");
      setShowExpenseModal(false);
      setExpenseForm({
        category: "",
        description: "",
        amount: "",
        date: new Date().toISOString().split('T')[0]
      });
      fetchData();
    } catch (error) {
      toast.error("Failed to record expense");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteIncome = async (incomeId) => {
    if (!window.confirm("Delete this income entry?")) return;
    try {
      await apiClient.delete(`/income/${incomeId}`);
      toast.success("Income deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete income");
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    if (!window.confirm("Delete this expense?")) return;
    try {
      await apiClient.delete(`/expenses/${expenseId}`);
      toast.success("Expense deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete expense");
    }
  };

  const handleCalculate = async () => {
    if (!calcForm.area_sqft || !calcForm.rate_per_sqft) {
      toast.error("Please enter area and rate");
      return;
    }
    
    try {
      const response = await apiClient.post("/calculate/rate", {
        area_sqft: parseFloat(calcForm.area_sqft),
        rate_per_sqft: parseFloat(calcForm.rate_per_sqft),
        discount_percent: parseFloat(calcForm.discount_percent) || 0,
        gst_percentage: parseFloat(calcForm.gst_percentage) || 18
      });
      setCalcResult(response.data);
    } catch (error) {
      toast.error("Calculation failed");
    }
  };

  const handleExport = async (type) => {
    try {
      const response = await apiClient.get(`/export/${type}?format=json`);
      const blob = new Blob([JSON.stringify(response.data.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = response.data.filename;
      a.click();
      toast.success(`${type} exported!`);
    } catch (error) {
      toast.error(`Failed to export ${type}`);
    }
  };

  const handleClearData = async () => {
    if (clearForm.confirm_text !== "DELETE ALL DATA") {
      toast.error("Please type 'DELETE ALL DATA' to confirm");
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await apiClient.post("/admin/clear-data", {
        password: clearForm.password,
        confirm_text: clearForm.confirm_text,
        entities: clearForm.entities.length > 0 ? clearForm.entities : []
      });
      toast.success("Data cleared successfully!");
      setShowClearDataModal(false);
      setClearForm({ password: "", confirm_text: "", entities: [] });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to clear data");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClearEverything = async () => {
    if (clearEverythingForm.confirm_text !== "DELETE EVERYTHING PERMANENTLY") {
      toast.error("Please type 'DELETE EVERYTHING PERMANENTLY' to confirm");
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await apiClient.post("/admin/clear-everything", null, {
        params: {
          password: clearEverythingForm.password,
          confirm_text: clearEverythingForm.confirm_text
        }
      });
      toast.success("All data deleted. You will be logged out.");
      setShowClearEverythingModal(false);
      setClearEverythingForm({ password: "", confirm_text: "" });
      // Logout user after clearing everything
      setTimeout(() => {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        window.location.href = "/login";
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to clear data");
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  // Calculate total sqft from projects
  const totalSqft = projects.reduce((sum, p) => sum + (p.final_sqft || p.approximate_sqft || 0), 0);
  const incomePerSqft = totalSqft > 0 ? (summary?.total_income || 0) / totalSqft : 0;
  const expensePerSqft = totalSqft > 0 ? (summary?.total_expenses || 0) / totalSqft : 0;
  const profitPerSqft = incomePerSqft - expensePerSqft;


  if (!isFounder) {
    return (
      <div className="page-content">
        <div className="text-center py-20">
          <PieChart className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Access Restricted</h2>
          <p className="text-gray-500">Only founders can access the accounts section.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="page-content">
        <div className="space-y-6">
          <div className="h-8 skeleton w-1/4"></div>
          <div className="grid grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (<div key={i} className="h-32 skeleton"></div>))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content animate-fade-in" data-testid="accounts-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Accounts</h1>
          <p className="page-subtitle">Financial overview with rate per sqft calculations</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowCalculatorModal(true)} className="border-[#262626]" data-testid="rate-calculator-btn">
            <Calculator className="w-4 h-4 mr-2" /> Rate Calculator
          </Button>
          <Button variant="outline" onClick={() => handleExport("accounts")} className="border-[#262626]" data-testid="export-accounts-btn">
            <Download className="w-4 h-4 mr-2" /> Export Report
          </Button>
          <Button onClick={() => setShowIncomeModal(true)} className="bg-[#10B981] hover:bg-[#059669]" data-testid="add-income-btn">
            <ArrowUpRight className="w-4 h-4 mr-2" /> Add Income
          </Button>
          <Button onClick={() => setShowExpenseModal(true)} className="bg-[#EF4444] hover:bg-[#DC2626]" data-testid="add-expense-btn">
            <ArrowDownRight className="w-4 h-4 mr-2" /> Add Expense
          </Button>
        </div>
      </div>

      {/* Summary Cards - Updated with Rate per Sqft */}
      <div className="grid grid-cols-6 gap-4 mb-8">
        <div className="bg-[#1A1A1A] border border-[#262626] rounded p-5 card-hover animate-fade-in-up stagger-1">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded bg-[#10B981]/20 flex items-center justify-center">
              <Wallet className="w-4 h-4 text-[#10B981]" />
            </div>
            <p className="text-xs text-gray-500 uppercase tracking-wider">Total Income</p>
          </div>
          <p className="text-2xl font-bold text-[#10B981]">{formatCurrency(summary?.total_income)}</p>
          <p className="text-xs text-gray-500 mt-1">₹{incomePerSqft.toFixed(2)}/sqft</p>
        </div>
        
        <div className="bg-[#1A1A1A] border border-[#262626] rounded p-5 card-hover animate-fade-in-up stagger-2">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded bg-[#3B82F6]/20 flex items-center justify-center">
              <TrendingUp className="w-4 h-4 text-[#3B82F6]" />
            </div>
            <p className="text-xs text-gray-500 uppercase tracking-wider">Project Revenue</p>
          </div>
          <p className="text-2xl font-bold text-[#3B82F6]">{formatCurrency(summary?.total_revenue)}</p>
        </div>
        
        <div className="bg-[#1A1A1A] border border-[#262626] rounded p-5 card-hover animate-fade-in-up stagger-3">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded bg-red-500/20 flex items-center justify-center">
              <TrendingDown className="w-4 h-4 text-red-400" />
            </div>
            <p className="text-xs text-gray-500 uppercase tracking-wider">Total Expenses</p>
          </div>
          <p className="text-2xl font-bold text-red-400">{formatCurrency(summary?.total_expenses)}</p>
          <p className="text-xs text-gray-500 mt-1">₹{expensePerSqft.toFixed(2)}/sqft</p>
        </div>
        
        <div className="bg-[#1A1A1A] border border-[#262626] rounded p-5 card-hover animate-fade-in-up stagger-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded bg-[#A78BFA]/20 flex items-center justify-center">
              <IndianRupee className="w-4 h-4 text-[#A78BFA]" />
            </div>
            <p className="text-xs text-gray-500 uppercase tracking-wider">Net Profit</p>
          </div>
          <p className={`text-2xl font-bold ${summary?.net_profit >= 0 ? 'text-[#10B981]' : 'text-red-400'}`}>
            {formatCurrency(summary?.net_profit)}
          </p>
          <p className={`text-xs mt-1 ${profitPerSqft >= 0 ? 'text-[#10B981]' : 'text-red-400'}`}>
            ₹{profitPerSqft.toFixed(2)}/sqft
          </p>
        </div>
        
        <div className="bg-[#1A1A1A] border border-[#262626] rounded p-5 card-hover animate-fade-in-up stagger-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded bg-[#F59E0B]/20 flex items-center justify-center">
              <Receipt className="w-4 h-4 text-[#F59E0B]" />
            </div>
            <p className="text-xs text-gray-500 uppercase tracking-wider">Pending</p>
          </div>
          <p className="text-2xl font-bold text-[#F59E0B]">{formatCurrency(summary?.pending_receivables)}</p>
        </div>
        
        <div className="bg-[#1A1A1A] border border-[#262626] rounded p-5 card-hover animate-fade-in-up stagger-6">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded bg-[#FF4500]/20 flex items-center justify-center">
              <SquareAsterisk className="w-4 h-4 text-[#FF4500]" />
            </div>
            <p className="text-xs text-gray-500 uppercase tracking-wider">Total Sqft</p>
          </div>
          <p className="text-2xl font-bold text-white">{totalSqft.toLocaleString()}</p>
          <p className="text-xs text-gray-500 mt-1">{projects.length} projects</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-[#1A1A1A] border border-[#262626] p-1">
          <TabsTrigger value="overview" className="data-[state=active]:bg-[#FF4500] data-[state=active]:text-white">Overview</TabsTrigger>
          <TabsTrigger value="income" className="data-[state=active]:bg-[#FF4500] data-[state=active]:text-white">
            Income {income.length > 0 && <span className="ml-1 px-1.5 py-0.5 rounded bg-white/20 text-xs">{income.length}</span>}
          </TabsTrigger>
          <TabsTrigger value="expenses" className="data-[state=active]:bg-[#FF4500] data-[state=active]:text-white">
            Expenses {expenses.length > 0 && <span className="ml-1 px-1.5 py-0.5 rounded bg-white/20 text-xs">{expenses.length}</span>}
          </TabsTrigger>
          <TabsTrigger value="rates" className="data-[state=active]:bg-[#FF4500] data-[state=active]:text-white">
            Rate Analysis
          </TabsTrigger>
          <TabsTrigger value="admin" className="data-[state=active]:bg-[#FF4500] data-[state=active]:text-white">
            Admin
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="animate-fade-in">
          <div className="grid grid-cols-12 gap-6">
            {/* Income by Category */}
            <div className="col-span-12 lg:col-span-6">
              <div className="bg-[#1A1A1A] border border-[#262626] rounded p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <ArrowUpRight className="w-5 h-5 text-[#10B981]" /> Income by Category
                </h3>
                {summary?.income_by_category && Object.keys(summary.income_by_category).length > 0 ? (
                  <div className="space-y-3">
                    {Object.entries(summary.income_by_category).map(([category, amount]) => (
                      <div key={category} className="flex items-center justify-between p-3 bg-[#171717] rounded">
                        <span className="text-gray-300 capitalize">{category.replace('_', ' ')}</span>
                        <span className="font-semibold text-[#10B981]">{formatCurrency(amount)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No income recorded yet</p>
                )}
              </div>
            </div>

            {/* Revenue by Project Type */}
            <div className="col-span-12 lg:col-span-6">
              <div className="bg-[#1A1A1A] border border-[#262626] rounded p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Revenue by Project Type</h3>
                <div className="space-y-4">
                  {summary?.revenue_by_type && Object.entries(summary.revenue_by_type).map(([type, amount]) => (
                    <div key={type} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${
                          type === 'architecture' ? 'bg-[#FF4500]' :
                          type === 'interior' ? 'bg-[#14B8A6]' : 'bg-[#A78BFA]'
                        }`} />
                        <span className="text-gray-300 capitalize">{type}</span>
                      </div>
                      <span className="font-semibold text-white">{formatCurrency(amount)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Expenses by Category */}
            <div className="col-span-12">
              <div className="bg-[#1A1A1A] border border-[#262626] rounded p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <ArrowDownRight className="w-5 h-5 text-red-400" /> Expenses by Category
                </h3>
                {summary?.expenses_by_category && Object.keys(summary.expenses_by_category).length > 0 ? (
                  <div className="grid grid-cols-3 gap-3">
                    {Object.entries(summary.expenses_by_category).map(([category, amount]) => (
                      <div key={category} className="flex items-center justify-between p-3 bg-[#171717] rounded">
                        <span className="text-gray-300">{category}</span>
                        <span className="font-semibold text-red-400">{formatCurrency(amount)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No expenses recorded</p>
                )}
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Income Tab */}
        <TabsContent value="income" className="animate-fade-in">
          <div className="bg-[#1A1A1A] border border-[#262626] rounded p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">All Income</h3>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => handleExport("payments")} className="border-[#262626]">
                  <Download className="w-4 h-4 mr-2" /> Export
                </Button>
                <Button onClick={() => setShowIncomeModal(true)} className="bg-[#10B981] hover:bg-[#059669]">
                  <Plus className="w-4 h-4 mr-2" /> Add Income
                </Button>
              </div>
            </div>
            {income.length > 0 ? (
              <table className="data-table w-full">
                <thead>
                  <tr>
                    <th className="px-4">Date</th>
                    <th className="px-4">Category</th>
                    <th className="px-4">Description</th>
                    <th className="px-4">Project</th>
                    <th className="px-4 text-right">Amount</th>
                    <th className="px-4">Method</th>
                    <th className="px-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {income.map((item) => (
                    <tr key={item.income_id}>
                      <td className="px-4 text-gray-400">{item.date}</td>
                      <td className="px-4"><span className="badge bg-[#10B981]/20 text-[#10B981] capitalize">{item.category.replace('_', ' ')}</span></td>
                      <td className="px-4 text-white">{item.description}</td>
                      <td className="px-4 text-gray-400">{item.project_name || '-'}</td>
                      <td className="px-4 text-right font-semibold text-[#10B981]">{formatCurrency(item.amount)}</td>
                      <td className="px-4 text-gray-400">{item.payment_method || '-'}</td>
                      <td className="px-4">
                        <button onClick={() => handleDeleteIncome(item.income_id)} className="p-1.5 rounded hover:bg-red-900/20 text-red-400">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-gray-500 text-center py-8">No income recorded yet</p>
            )}
          </div>
        </TabsContent>

        {/* Expenses Tab */}
        <TabsContent value="expenses" className="animate-fade-in">
          <div className="bg-[#1A1A1A] border border-[#262626] rounded p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">All Expenses</h3>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => handleExport("accounts")} className="border-[#262626]">
                  <Download className="w-4 h-4 mr-2" /> Export
                </Button>
                <Button onClick={() => setShowExpenseModal(true)} className="bg-[#EF4444] hover:bg-[#DC2626]">
                  <Plus className="w-4 h-4 mr-2" /> Add Expense
                </Button>
              </div>
            </div>
            {expenses.length > 0 ? (
              <table className="data-table w-full">
                <thead>
                  <tr>
                    <th className="px-4">Date</th>
                    <th className="px-4">Category</th>
                    <th className="px-4">Description</th>
                    <th className="px-4 text-right">Amount</th>
                    <th className="px-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {expenses.map((expense) => (
                    <tr key={expense.expense_id}>
                      <td className="px-4 text-gray-400">{expense.date}</td>
                      <td className="px-4"><span className="badge bg-gray-700 text-gray-300">{expense.category}</span></td>
                      <td className="px-4 text-white">{expense.description}</td>
                      <td className="px-4 text-right font-semibold text-red-400">{formatCurrency(expense.amount)}</td>
                      <td className="px-4">
                        <button onClick={() => handleDeleteExpense(expense.expense_id)} className="p-1.5 rounded hover:bg-red-900/20 text-red-400">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-gray-500 text-center py-8">No expenses recorded yet</p>
            )}
          </div>
        </TabsContent>

        {/* Rate Analysis Tab */}
        <TabsContent value="rates" className="animate-fade-in">
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-12 lg:col-span-6">
              <div className="bg-[#1A1A1A] border border-[#262626] rounded p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Rate Per Sqft Analysis</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-[#171717] rounded">
                    <div>
                      <p className="text-gray-400 text-sm">Average Income</p>
                      <p className="text-xl font-bold text-[#10B981]">₹{incomePerSqft.toFixed(2)}</p>
                    </div>
                    <span className="text-gray-500">per sqft</span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-[#171717] rounded">
                    <div>
                      <p className="text-gray-400 text-sm">Average Expense</p>
                      <p className="text-xl font-bold text-red-400">₹{expensePerSqft.toFixed(2)}</p>
                    </div>
                    <span className="text-gray-500">per sqft</span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-[#171717] rounded border-t-2 border-[#FF4500]">
                    <div>
                      <p className="text-gray-400 text-sm">Net Profit</p>
                      <p className={`text-xl font-bold ${profitPerSqft >= 0 ? 'text-[#10B981]' : 'text-red-400'}`}>
                        ₹{profitPerSqft.toFixed(2)}
                      </p>
                    </div>
                    <span className="text-gray-500">per sqft</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="col-span-12 lg:col-span-6">
              <div className="bg-[#1A1A1A] border border-[#262626] rounded p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Project Type Rates</h3>
                <div className="space-y-4">
                  {["architecture", "interior", "turnkey"].map((type) => {
                    const typeProjects = projects.filter(p => p.project_type === type);
                    const typeSqft = typeProjects.reduce((sum, p) => sum + (p.final_sqft || p.approximate_sqft || 0), 0);
                    const avgRate = typeProjects.length > 0 
                      ? typeProjects.reduce((sum, p) => sum + (p.rate_per_sqft || 0), 0) / typeProjects.length 
                      : 0;
                    
                    return (
                      <div key={type} className="flex items-center justify-between p-4 bg-[#171717] rounded">
                        <div className="flex items-center gap-3">
                          <div className={`w-3 h-3 rounded-full ${
                            type === 'architecture' ? 'bg-[#FF4500]' :
                            type === 'interior' ? 'bg-[#14B8A6]' : 'bg-[#A78BFA]'
                          }`} />
                          <div>
                            <p className="text-white capitalize">{type}</p>
                            <p className="text-xs text-gray-500">{typeProjects.length} projects • {typeSqft.toLocaleString()} sqft</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-white">₹{avgRate.toFixed(0)}</p>
                          <p className="text-xs text-gray-500">avg rate/sqft</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Admin Tab */}
        <TabsContent value="admin" className="animate-fade-in">
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-12 lg:col-span-6">
              <div className="bg-[#1A1A1A] border border-[#262626] rounded p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Download className="w-5 h-5 text-[#3B82F6]" /> Export Reports
                </h3>
                <div className="space-y-3">
                  <button onClick={() => handleExport("clients")} className="w-full p-4 bg-[#171717] rounded border border-[#262626] hover:border-[#FF4500] transition-all text-left group">
                    <p className="text-white font-medium">Clients Report</p>
                    <p className="text-xs text-gray-500">Export all client data</p>
                  </button>
                  <button onClick={() => handleExport("projects")} className="w-full p-4 bg-[#171717] rounded border border-[#262626] hover:border-[#FF4500] transition-all text-left group">
                    <p className="text-white font-medium">Projects Report</p>
                    <p className="text-xs text-gray-500">Export all projects with rate calculations</p>
                  </button>
                  <button onClick={() => handleExport("payments")} className="w-full p-4 bg-[#171717] rounded border border-[#262626] hover:border-[#FF4500] transition-all text-left group">
                    <p className="text-white font-medium">Payments Report</p>
                    <p className="text-xs text-gray-500">Export all payments with status</p>
                  </button>
                  <button onClick={() => handleExport("accounts")} className="w-full p-4 bg-[#171717] rounded border border-[#262626] hover:border-[#FF4500] transition-all text-left group">
                    <p className="text-white font-medium">Full Accounts Report</p>
                    <p className="text-xs text-gray-500">Complete financial report with rate per sqft</p>
                  </button>
                </div>
              </div>
            </div>
            
            <div className="col-span-12 lg:col-span-6">
              <div className="bg-[#1A1A1A] border border-red-900/50 rounded p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-400" /> Danger Zone
                </h3>
                <p className="text-gray-400 text-sm mb-4">
                  Clear data from the system. This action cannot be undone. You will need to enter your password and type a confirmation text.
                </p>
                <div className="space-y-3">
                  <Button 
                    variant="outline" 
                    onClick={() => setShowClearDataModal(true)} 
                    className="w-full border-red-900/50 text-red-400 hover:bg-red-900/20"
                    data-testid="clear-data-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" /> Clear Selected Data
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => setShowClearEverythingModal(true)} 
                    className="w-full border-red-600 text-red-500 hover:bg-red-900/30 font-semibold"
                    data-testid="clear-everything-btn"
                  >
                    <AlertTriangle className="w-4 h-4 mr-2" /> Clear EVERYTHING (Including Accounts)
                  </Button>
                  <p className="text-xs text-red-400/70 text-center">
                    Warning: "Clear Everything" will delete ALL data including user accounts. You will be logged out.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <AddIncomeModal
        open={showIncomeModal}
        onOpenChange={setShowIncomeModal}
        incomeForm={incomeForm}
        setIncomeForm={setIncomeForm}
        clients={clients}
        projects={projects}
        payments={payments}
        handleSubmitIncome={handleSubmitIncome}
        submitting={submitting}
      />

      <AddExpenseModal
        open={showExpenseModal}
        onOpenChange={setShowExpenseModal}
        expenseForm={expenseForm}
        setExpenseForm={setExpenseForm}
        handleSubmitExpense={handleSubmitExpense}
        submitting={submitting}
      />

      <RateCalculatorModal
        open={showCalculatorModal}
        onOpenChange={setShowCalculatorModal}
        calcForm={calcForm}
        setCalcForm={setCalcForm}
        calcResult={calcResult}
        handleCalculate={handleCalculate}
      />

      <ClearDataModal
        open={showClearDataModal}
        onOpenChange={setShowClearDataModal}
        clearForm={clearForm}
        setClearForm={setClearForm}
        handleClearData={handleClearData}
        submitting={submitting}
      />

      <ClearEverythingModal
        open={showClearEverythingModal}
        onOpenChange={setShowClearEverythingModal}
        clearEverythingForm={clearEverythingForm}
        setClearEverythingForm={setClearEverythingForm}
        handleClearEverything={handleClearEverything}
        submitting={submitting}
      />
    </div>
  );
}
